import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaGlobe, FaTwitter, FaYoutube, FaDiscord, FaBlog, FaCheck, FaTimes, FaExternalLinkAlt, FaShareAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';

import Header from '../components/Header';
import servicesDataRaw from '../data/services.json';
import contentData from '../data/content.json';
import TagButton from '../components/TagButton';
import Footer from '../components/Footer';
import { Helmet } from 'react-helmet-async';

const servicesData = servicesDataRaw;

function DetailPage() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const serviceData = servicesData.find(service => service.id === serviceId);
  const [activeSection, setActiveSection] = useState('overview');

  // 관련 콘텐츠 필터링
  const relatedContent = useMemo(() => {
    if (!serviceData) return { allContent: [] };

    const filteredContent = contentData.filter(item =>
      (item.tags && item.tags.includes(serviceData.id)) || (item.tag && item.tag === serviceData.id)
    );

    return {
      allContent: filteredContent.filter(item =>
        item.type === 'youtube' || item.type === 'article' || item.type === 'post' || item.type === 'social'
      )
    };
  }, [serviceData]);

  // 서비스 로고 경로
  const serviceLogoUrl = useMemo(() => {
    if (!serviceData) return '';
    return `/Images/Logos/${serviceData.id.replace(/ /g, '')}.png`;
  }, [serviceData]);

  // 스크롤 감지하여 목차 활성화
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'features', 'pros-cons', 'pricing', 'related'];
      const scrollPosition = window.scrollY + 150;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(section);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!serviceData) {
    return (
      <div className="flex flex-col h-screen bg-gray-50 dark:bg-slate-900">
        <Header showFitViewButton={false} />
        <main className="flex-grow flex items-center justify-center text-center">
          <div className="p-8 bg-white dark:bg-gray-800 rounded-2xl shadow-xl">
            <h1 className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Service Not Found</h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">요청하신 서비스를 찾을 수 없습니다.</p>
            <Link to="/" className="px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors font-medium">
              홈으로 돌아가기
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${serviceData.name} - AI 서비스 상세 | WithAI`}</title>
        <meta name="description" content={serviceData.summary || serviceData.introduction} />
      </Helmet>

      <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-slate-900 font-sans transition-colors duration-300">
        <div className="fixed top-0 left-0 w-full z-50">
          <Header showFitViewButton={false} />
        </div>

        <main className="flex-grow pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">

          {/* Breadcrumb & Back */}
          <nav className="flex items-center justify-between mb-8 text-sm">
            <button onClick={() => navigate(-1)} className="flex items-center text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors">
              <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              돌아가기
            </button>
            <div className="flex items-center space-x-2 text-gray-400">
              <span>Home</span>
              <span>/</span>
              <span>Service</span>
              <span>/</span>
              <span className="text-gray-900 dark:text-white font-medium">{serviceData.name}</span>
            </div>
          </nav>

          {/* Hero Section */}
          <section className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden mb-10 border border-gray-100 dark:border-gray-700">
            <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-r from-blue-500/10 to-purple-500/10 dark:from-blue-900/20 dark:to-purple-900/20"></div>
            <div className="relative p-8 md:p-12 flex flex-col md:flex-row items-center md:items-start gap-8">
              <div className="flex-shrink-0 bg-white dark:bg-gray-700 p-4 rounded-2xl shadow-md z-10">
                <img
                  src={serviceLogoUrl}
                  alt={serviceData.name}
                  className="w-32 h-32 object-contain"
                  onError={e => { e.target.onerror = null; e.target.src = '/Images/Logos/Base.jpg'; }}
                />
              </div>
              <div className="flex-grow text-center md:text-left z-10">
                <div className="flex flex-col md:flex-row items-center md:items-baseline gap-3 mb-2">
                  <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-white tracking-tight">{serviceData.name}</h1>
                  <span className="px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-sm font-semibold">
                    {serviceData.category}
                  </span>
                </div>
                <p className="text-xl text-gray-600 dark:text-gray-300 mb-6 max-w-3xl leading-relaxed">
                  {serviceData.summary || serviceData.introduction}
                </p>
                <div className="flex flex-wrap justify-center md:justify-start gap-3">
                  {serviceData.link && (
                    <a href={serviceData.link} target="_blank" rel="noreferrer" className="inline-flex items-center px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg hover:shadow-blue-500/30 transition-all transform hover:-translate-y-0.5">
                      <FaExternalLinkAlt className="mr-2" /> 웹사이트 방문
                    </a>
                  )}
                  {/* Social Links */}
                  {serviceData.socialLinks?.twitter && (
                    <a href={serviceData.socialLinks.twitter} target="_blank" rel="noreferrer" className="p-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-sky-500 hover:text-white transition-colors">
                      <FaTwitter size={20} />
                    </a>
                  )}
                  {serviceData.socialLinks?.youtube && (
                    <a href={serviceData.socialLinks.youtube} target="_blank" rel="noreferrer" className="p-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-red-500 hover:text-white transition-colors">
                      <FaYoutube size={20} />
                    </a>
                  )}
                  {serviceData.socialLinks?.discord && (
                    <a href={serviceData.socialLinks.discord} target="_blank" rel="noreferrer" className="p-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-indigo-500 hover:text-white transition-colors">
                      <FaDiscord size={20} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Sidebar (TOC & Quick Info) */}
            <aside className="lg:col-span-3 lg:sticky lg:top-28 h-fit space-y-6 order-2 lg:order-1">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                  목차
                </h3>
                <nav className="space-y-1">
                  {['overview', 'features', 'pros-cons', 'pricing', 'related'].map((section) => (
                    <a
                      key={section}
                      href={`#${section}`}
                      className={`block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeSection === section
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                    >
                      {section === 'overview' && '서비스 개요'}
                      {section === 'features' && '주요 기능'}
                      {section === 'pros-cons' && '장점 및 단점'}
                      {section === 'pricing' && '가격 정책'}
                      {section === 'related' && '관련 콘텐츠'}
                    </a>
                  ))}
                </nav>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">정보</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold mb-1">API 지원</p>
                    <div className="flex items-center">
                      {serviceData.apiSupport ? (
                        <span className="flex items-center text-green-600 dark:text-green-400 font-medium text-sm">
                          <FaCheck className="mr-1.5" size={12} /> 지원함
                        </span>
                      ) : (
                        <span className="flex items-center text-gray-500 dark:text-gray-400 font-medium text-sm">
                          <FaTimes className="mr-1.5" size={12} /> 미지원
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold mb-1">플랫폼</p>
                    <div className="flex flex-wrap gap-1.5">
                      {serviceData.platforms?.map((p, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs font-medium text-gray-700 dark:text-gray-300">{p}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold mb-1">태그</p>
                    <div className="flex flex-wrap gap-1.5">
                      {serviceData.tags?.map((tag, i) => (
                        <span key={i} className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded text-xs font-medium">#{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <div className="lg:col-span-9 space-y-10 order-1 lg:order-2">

              {/* Overview */}
              <section id="overview" className="scroll-mt-28">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                    <span className="w-2 h-8 bg-blue-500 rounded-full mr-3"></span>
                    서비스 개요
                  </h2>
                  <div className="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed">
                    <p className="whitespace-pre-line">{serviceData.introduction || serviceData.summary}</p>
                    {serviceData.Use && (
                      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-100 dark:border-gray-700">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-2">💡 어떻게 사용하나요?</h4>
                        <p>{serviceData.Use}</p>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* Features */}
              <section id="features" className="scroll-mt-28">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                    <span className="w-2 h-8 bg-purple-500 rounded-full mr-3"></span>
                    주요 기능
                  </h2>
                  {serviceData.features && serviceData.features.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {serviceData.features.map((feature, idx) => (
                        <div key={idx} className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700 hover:border-purple-200 dark:hover:border-purple-800 transition-colors">
                          <h4 className="font-bold text-gray-900 dark:text-white mb-2 flex items-start">
                            <span className="text-purple-500 mr-2">•</span>
                            {feature}
                          </h4>
                          {serviceData.features_detail && serviceData.features_detail[idx] && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 pl-4">
                              {serviceData.features_detail[idx]}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">등록된 주요 기능 정보가 없습니다.</p>
                  )}
                </div>
              </section>

              {/* Pros & Cons */}
              <section id="pros-cons" className="scroll-mt-28">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
                    <h3 className="text-xl font-bold text-green-600 dark:text-green-400 mb-4 flex items-center">
                      <FaCheck className="mr-2" /> 장점
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                      {serviceData.merit || '정보 없음'}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
                    <h3 className="text-xl font-bold text-red-500 dark:text-red-400 mb-4 flex items-center">
                      <FaTimes className="mr-2" /> 단점
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                      {serviceData.disadvantage || '정보 없음'}
                    </p>
                  </div>
                </div>
              </section>

              {/* Pricing */}
              <section id="pricing" className="scroll-mt-28">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                    <span className="w-2 h-8 bg-green-500 rounded-full mr-3"></span>
                    가격 정책
                  </h2>
                  {serviceData.pricing && serviceData.pricing.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {serviceData.pricing.map((plan, idx) => (
                        <div key={idx} className="relative p-6 rounded-2xl border border-gray-200 dark:border-gray-700 flex flex-col hover:shadow-lg transition-shadow bg-gray-50 dark:bg-gray-700/20">
                          <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{plan.plan}</h4>
                          <div className="text-2xl font-extrabold text-green-600 dark:text-green-400 mb-4">{plan.price}</div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 flex-grow">{plan.description}</p>
                          {plan.features && (
                            <ul className="space-y-2 mb-4">
                              {plan.features.map((f, i) => (
                                <li key={i} className="text-sm text-gray-700 dark:text-gray-300 flex items-start">
                                  <FaCheck className="text-green-500 mr-2 mt-0.5 flex-shrink-0" size={12} />
                                  {f}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">가격 정보가 없습니다.</p>
                  )}
                </div>
              </section>

              {/* Related Content */}
              {relatedContent.allContent.length > 0 && (
                <section id="related" className="scroll-mt-28">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">관련 콘텐츠</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {relatedContent.allContent.map(item => (
                      <Link
                        key={item.contentId}
                        to={`/content/${item.contentId}`}
                        className="group bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-md transition-all"
                      >
                        <div className="relative aspect-video overflow-hidden bg-gray-200 dark:bg-gray-700">
                          {item.thumbnail ? (
                            <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">No Image</div>
                          )}
                          <div className="absolute top-2 left-2">
                            {item.type === 'youtube' && <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">YouTube</span>}
                            {item.type === 'article' && <span className="px-2 py-1 bg-blue-600 text-white text-xs font-bold rounded">Article</span>}
                          </div>
                        </div>
                        <div className="p-4">
                          <h4 className="font-bold text-gray-900 dark:text-white line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                            {item.title}
                          </h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 line-clamp-2">
                            {item.summary}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}

export default DetailPage;
